import React, { useState, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Chip,
  IconButton,
  Box
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import debounce from 'lodash/debounce';

export default function CapabilitiesTable({ 
  data = [], 
  onUpdate, 
  columnHeaders = ['Capability', '1-2 skills you already excel in'],
  placeholder = 'Add new capability here'
}) {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [localCapabilities, setLocalCapabilities] = useState(data);

  // Debounced update function for capabilities
  const debouncedUpdate = useCallback(
    debounce((updatedData) => {
      onUpdate(updatedData);
    }, 500),
    []
  );

  const handleCapabilityChange = (index, value) => {
    const updatedData = [...localCapabilities];
    if (index === localCapabilities.length) {
      // This is the empty row - create a new entry
      updatedData.push({ capability: value, skills: [] });
    } else {
      updatedData[index] = { ...updatedData[index], capability: value };
    }
    
    // Update local state immediately
    setLocalCapabilities(updatedData);
    
    // Debounce the update to parent component
    debouncedUpdate(updatedData);
  };

  const handleSkillsChange = (index, value) => {
    setInputValues(prev => ({
      ...prev,
      [index]: value
    }));

    if (value.endsWith(',')) {
      const skill = value.slice(0, -1).trim();
      if (skill) {
        const updatedData = [...localCapabilities];
        const currentSkills = updatedData[index]?.skills || [];
        if (!currentSkills.includes(skill)) {
          if (index === localCapabilities.length) {
            // This is the empty row - create a new entry
            updatedData.push({ capability: '', skills: [skill] });
          } else {
            updatedData[index] = {
              ...updatedData[index],
              skills: [...currentSkills, skill]
            };
          }
          setLocalCapabilities(updatedData);
          onUpdate(updatedData);
        }
        // Clear the input
        setInputValues(prev => ({
          ...prev,
          [index]: ''
        }));
      }
    }
  };

  const handleDeleteSkill = (rowIndex, skill) => {
    const updatedData = [...localCapabilities];
    updatedData[rowIndex] = {
      ...updatedData[rowIndex],
      skills: updatedData[rowIndex].skills.filter(s => s !== skill)
    };
    setLocalCapabilities(updatedData);
    onUpdate(updatedData);
  };

  const handleDeleteRow = (index) => {
    const updatedData = localCapabilities.filter((_, i) => i !== index);
    setLocalCapabilities(updatedData);
    onUpdate(updatedData);
  };

  // Update local state when props change
  React.useEffect(() => {
    setLocalCapabilities(data);
  }, [data]);

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead sx={{ backgroundColor: '#F0F0F0' }}>
          <TableRow>
            <TableCell width="40%" style={{ fontWeight: 'bold' }}>{columnHeaders[0]}</TableCell>
            <TableCell width="60%" style={{ fontWeight: 'bold' }}>{columnHeaders[1]}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...localCapabilities, { capability: '', skills: [] }].map((row, index) => (
            <TableRow
              key={index}
              onMouseEnter={() => setHoveredRow(index)}
              onMouseLeave={() => setHoveredRow(null)}
              sx={{
                height: '60px',
                '&:hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    value={row.capability}
                    onChange={(e) => handleCapabilityChange(index, e.target.value)}
                    placeholder={index === localCapabilities.length ? placeholder : ''}
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' }
                    }}
                  />
                  {hoveredRow === index && index !== localCapabilities.length && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow(index)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  {row.skills?.map((skill, skillIndex) => (
                    <Chip
                      key={skillIndex}
                      label={skill}
                      onDelete={() => handleDeleteSkill(index, skill)}
                      sx={{ 
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        '& .MuiChip-deleteIcon': {
                          color: '#666666',
                          '&:hover': {
                            color: 'error.main'
                          }
                        }
                      }}
                    />
                  ))}
                  <TextField
                    value={inputValues[index] || ''}
                    onChange={(e) => handleSkillsChange(index, e.target.value)}
                    placeholder="Add skills, separate with comma"
                    variant="standard"
                    sx={{
                      flex: 1,
                      '& .MuiInput-underline:before': { borderBottom: 'none' },
                      '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(0, 0, 0, 0.42)' }
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
} 